/**
 * "Preferences" on My account: the display time zone and whether to email about interview
 * bookings. Shown to the roles that have interviews (the same five as the Interviews card).
 *
 * Deliberately NOT here yet: a language for candidate summaries. Rio only produces English
 * today, so there is nothing to choose, and a control that does nothing would claim otherwise.
 * It is added when a second language is switched on. The stored override is carried through
 * unchanged on every save, so saving here never clears one set elsewhere.
 */

import { memo, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Select, Spin, Switch, Typography } from 'antd';
import { PreferencesState } from '../../usePreferences';
import { UserPreferences } from '../../types';
import styles from './index.module.css';

const { Text } = Typography;

const FALLBACK_ZONES = [
  'UTC',
  'Europe/London',
  'Europe/Amsterdam',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Asia/Kolkata',
  'Asia/Tokyo',
  'Australia/Sydney',
];

const browserZone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
};

const allZones = (): string[] => {
  const supported = (
    Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
  ).supportedValuesOf;
  try {
    const zones = supported?.('timeZone');
    if (zones && zones.length > 0) return zones;
  } catch {
    // Fall through to the short list.
  }
  return FALLBACK_ZONES;
};

interface Props {
  state: PreferencesState;
  onReload: () => void;
  onSave: (next: UserPreferences) => Promise<unknown>;
}

const PreferencesSection = ({ state, onReload, onSave }: Props) => {
  const [timezone, setTimezone] = useState<string | null>(null);
  const [emails, setEmails] = useState(true);
  const [saving, setSaving] = useState(false);
  const [outcome, setOutcome] = useState<'saved' | 'failed' | null>(null);

  const stored = state.status === 'ready' ? state.prefs : null;
  useEffect(() => {
    if (stored) {
      setTimezone(stored.timezone);
      setEmails(stored.emailBookingNotifications);
    }
  }, [stored]);

  const options = useMemo(() => {
    const zones = allZones();
    const withCurrent =
      stored?.timezone && !zones.includes(stored.timezone)
        ? [stored.timezone, ...zones]
        : zones;
    return withCurrent.map((zone) => ({ label: zone, value: zone }));
  }, [stored?.timezone]);

  if (state.status === 'loading') {
    return (
      <div className={styles.loading}>
        <Spin />
      </div>
    );
  }
  if (state.status === 'error' || !stored) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Your preferences could not be loaded."
        action={
          <Button size="small" onClick={onReload}>
            Try again
          </Button>
        }
      />
    );
  }

  const dirty =
    timezone !== stored.timezone || emails !== stored.emailBookingNotifications;

  const save = async () => {
    setSaving(true);
    setOutcome(null);
    try {
      await onSave({
        timezone,
        summaryLanguage: stored.summaryLanguage,
        emailBookingNotifications: emails,
      });
      setOutcome('saved');
    } catch {
      setOutcome('failed');
    } finally {
      setSaving(false);
    }
  };

  const zoneId = 'preferences-timezone';
  const emailId = 'preferences-booking-emails';

  return (
    <div className={styles.fields}>
      <div>
        <label htmlFor={zoneId} className={styles.label}>
          Time zone
        </label>
        <Select
          id={zoneId}
          showSearch
          allowClear
          className={styles.select}
          placeholder={`Browser default${
            browserZone() ? ` (${browserZone()})` : ''
          }`}
          options={options}
          value={timezone ?? undefined}
          onChange={(value) => setTimezone(value ?? null)}
          filterOption={(input, option) =>
            String(option?.value ?? '')
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        />
        <Text type="secondary" className={styles.hint}>
          Interview times on this page are shown in this zone. Leave it empty to
          see each interview in its own zone.
        </Text>
      </div>

      <div className={styles.row}>
        <Switch
          id={emailId}
          checked={emails}
          onChange={setEmails}
          aria-labelledby={`${emailId}-label`}
        />
        <label id={`${emailId}-label`} htmlFor={emailId}>
          Email me when an interview is booked or cancelled
        </label>
      </div>

      <div className={styles.actions}>
        <Button
          type="primary"
          onClick={save}
          loading={saving}
          disabled={!dirty || saving}
        >
          Save preferences
        </Button>
        {outcome === 'saved' && (
          <Text role="status" type="success">
            Saved.
          </Text>
        )}
        {outcome === 'failed' && (
          <Text role="alert" type="danger">
            Your preferences could not be saved. Try again.
          </Text>
        )}
      </div>
    </div>
  );
};

export default memo(PreferencesSection);
